import { useState, useEffect, useCallback } from 'react'
import styles from './SOSBreathingTimer.module.css'

interface Props {
  onDone: () => void
}

type BreathPhase = 'inhale' | 'hold' | 'exhale'

const INHALE_DURATION = 4
const HOLD_DURATION = 4
const EXHALE_DURATION = 6
const TOTAL_CYCLES = 3
const CYCLE_DURATION = INHALE_DURATION + HOLD_DURATION + EXHALE_DURATION

export default function SOSBreathingTimer({ onDone }: Props) {
  const [cycle, setCycle] = useState(1)
  const [phase, setPhase] = useState<BreathPhase>('inhale')
  const [secondsInPhase, setSecondsInPhase] = useState(0)
  const [totalElapsed, setTotalElapsed] = useState(0)

  const getPhaseLabel = useCallback((p: BreathPhase) => {
    switch (p) {
      case 'inhale': return 'Breathe In'
      case 'hold': return 'Hold'
      case 'exhale': return 'Breathe Out'
    }
  }, [])

  const getPhaseDuration = useCallback((p: BreathPhase) => {
    switch (p) {
      case 'inhale': return INHALE_DURATION
      case 'hold': return HOLD_DURATION
      case 'exhale': return EXHALE_DURATION
    }
  }, [])

  useEffect(() => {
    const timer = setInterval(() => {
      setTotalElapsed(prev => {
        const next = prev + 1
        const totalTime = TOTAL_CYCLES * CYCLE_DURATION

        if (next >= totalTime) {
          clearInterval(timer)
          onDone()
          return prev
        }

        const posInCycle = next % CYCLE_DURATION
        const currentCycle = Math.floor(next / CYCLE_DURATION) + 1
        setCycle(currentCycle)

        if (posInCycle < INHALE_DURATION) {
          setPhase('inhale')
          setSecondsInPhase(posInCycle)
        } else if (posInCycle < INHALE_DURATION + HOLD_DURATION) {
          setPhase('hold')
          setSecondsInPhase(posInCycle - INHALE_DURATION)
        } else {
          setPhase('exhale')
          setSecondsInPhase(posInCycle - INHALE_DURATION - HOLD_DURATION)
        }

        return next
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [onDone])

  const phaseDuration = getPhaseDuration(phase)
  const progress = secondsInPhase / phaseDuration
  const scale = phase === 'inhale' ? 0.6 + 0.4 * progress :
                phase === 'hold' ? 1.0 :
                1.0 - 0.4 * progress

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Breathe Together</h2>
      <p className={styles.subtitle}>Follow the circle with your child</p>

      <div className={styles.circleWrapper}>
        <div
          className={styles.circle}
          style={{ transform: `scale(${scale})` }}
        />
        <div className={styles.phaseLabel}>{getPhaseLabel(phase)}</div>
        <div className={styles.countdown}>
          {phaseDuration - secondsInPhase}
        </div>
      </div>

      <div className={styles.info}>
        <span>Cycle {cycle} of {TOTAL_CYCLES}</span>
        <span>{Math.max(0, TOTAL_CYCLES * CYCLE_DURATION - totalElapsed)}s remaining</span>
      </div>

      <button className={styles.skipButton} onClick={onDone}>
        Skip to Log
      </button>
    </div>
  )
}
